import countryLabels from 'react-phone-number-input/locale/en.json'

/** Structured address fields filled from a Google Places selection. */
export type ParsedGoogleAddress = {
  street: string
  city: string
  cp: string
  country: string
}

type AddressComponent = google.maps.GeocoderAddressComponent

/**
 * Google language codes we can send for a given app locale. Places and the
 * Geocoder speak plain language tags (`en`, `ar`, `fr`), so region-suffixed app
 * locales are reduced to their base language. `iv` is the pseudo-locale used for
 * i18n extraction and has no Google equivalent.
 */
export function googlePlacesLanguage(locale: string | undefined): string {
  if (!locale) return 'en'
  const [language] = locale.split('-')
  return !language || language === 'iv' ? 'en' : language.toLowerCase()
}

/**
 * Region (ccTLD) hint used to bias results towards where the user works, e.g.
 * `en-SA` biases towards Saudi Arabia. Locales without a region subtag get no
 * bias, which keeps worldwide addresses reachable.
 */
export function googlePlacesRegion(locale: string | undefined): string | undefined {
  const region = locale?.split('-')[1]
  return region ? region.toLowerCase() : undefined
}

function findComponent(components: AddressComponent[], type: string): AddressComponent | undefined {
  return components.find((component) => component.types.includes(type))
}

function longName(components: AddressComponent[], ...types: string[]): string {
  for (const type of types) {
    const match = findComponent(components, type)
    if (match?.long_name) return match.long_name
  }
  return ''
}

/**
 * Street line for the selected place.
 *
 * Component order is locale-dependent ("12 Rue de la Paix" but "Hauptstraße 5"),
 * and `address_components` carries no ordering information. Google already
 * formats the street line correctly in the first segment of `formatted_address`,
 * so prefer that whenever it actually refers to this street, and fall back to a
 * number-first composition otherwise (e.g. city-level results).
 */
function parseStreet(components: AddressComponent[], formattedAddress: string | undefined): string {
  const route = longName(components, 'route')
  if (!route) return ''

  const firstSegment = formattedAddress?.split(',')[0]?.trim()
  if (firstSegment?.includes(route)) return firstSegment

  const streetNumber = longName(components, 'street_number')
  return streetNumber ? `${streetNumber} ${route}` : route
}

/**
 * Map a Google geocoder result onto the patient address fields.
 *
 * `canonicalCountry` resolves the country to its English display name via the
 * ISO 3166-1 alpha-2 code rather than the localized name. SEHA stores English
 * display names because City and Country are dropdowns there, so the filled
 * value has to match the option list exactly.
 */
export function parseGoogleAddress(
  result: Pick<google.maps.GeocoderResult, 'address_components' | 'formatted_address'>,
  { canonicalCountry = false }: { canonicalCountry?: boolean } = {}
): ParsedGoogleAddress {
  const components = result.address_components ?? []

  const countryComponent = findComponent(components, 'country')
  const countryCode = countryComponent?.short_name as keyof typeof countryLabels | undefined
  const canonicalName = canonicalCountry && countryCode ? countryLabels[countryCode] : undefined

  return {
    street: parseStreet(components, result.formatted_address),
    // `postal_town` covers the UK, where Google omits `locality` for many addresses.
    city: longName(components, 'locality', 'postal_town', 'sublocality', 'administrative_area_level_2', 'administrative_area_level_1'),
    cp: longName(components, 'postal_code'),
    country: canonicalName ?? countryComponent?.long_name ?? '',
  }
}
